import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from './stores/auth-store'
import { useUiStore } from './stores/ui-store'
import ProtectedRoute, { deriveRole } from './components/ProtectedRoute'

// Pages — eager (musi być natychmiast dostępne przy pierwszym załadowaniu)
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'

// Pages — lazy (code-splitting per route, każda strona w osobnym chunku)
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const WspolnotyPage = lazy(() => import('./pages/WspolnotyPage'))
const WspolnotaDetailPage = lazy(() => import('./pages/WspolnotaDetailPage'))
const WspolnotaEditPage = lazy(() => import('./pages/WspolnotaEditPage'))
const AdministratorPage = lazy(() => import('./pages/AdministratorPage'))
const LokalePage = lazy(() => import('./pages/LokalePage'))
const LokalDetailPage = lazy(() => import('./pages/LokalDetailPage'))
const LicznikiPage = lazy(() => import('./pages/LicznikiPage'))
const LicznikiListPage = lazy(() => import('./pages/LicznikiListPage'))
const RozliczeniaPage = lazy(() => import('./pages/RozliczeniaPage'))
const HistoriaRozliczenPage = lazy(() => import('./pages/HistoriaRozliczenPage'))
const RozliczenieDetailPage = lazy(() => import('./pages/RozliczenieDetailPage'))
const RozliczeniePrintView = lazy(() => import('./pages/RozliczeniePrintView'))
const PozycjeCzynszowePage = lazy(() => import('./pages/PozycjeCzynszowePage'))
const StawkiMediowPage = lazy(() => import('./pages/StawkiMediowPage'))
const ZadluzeniaPage = lazy(() => import('./pages/ZadluzeniaPage'))
const PlatnosciPage = lazy(() => import('./pages/PlatnosciPage'))
const EmailHistoryPage = lazy(() => import('./pages/EmailHistoryPage'))
const MasoweOdczytyPage = lazy(() => import('./pages/MasoweOdczytyPage'))
const ImportBankowyPage = lazy(() => import('./pages/ImportBankowyPage'))
const RaportyPage = lazy(() => import('./pages/RaportyPage'))
const PowiadomieniaPage = lazy(() => import('./pages/PowiadomieniaPage'))
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))

// Resident Portal — lazy
const ResidentLoginPage = lazy(() => import('./pages/ResidentLoginPage'))
const ResidentActivatePage = lazy(() => import('./pages/ResidentActivatePage'))
const ResidentDashboardPage = lazy(() => import('./pages/ResidentDashboardPage'))
const ResidentPaymentsPage = lazy(() => import('./pages/ResidentPaymentsPage'))
const ResidentMetersPage = lazy(() => import('./pages/ResidentMetersPage'))
const ResidentSettlementsPage = lazy(() => import('./pages/ResidentSettlementsPage'))
const ResidentChangePasswordPage = lazy(() => import('./pages/ResidentChangePasswordPage'))
const ResidentLayout = lazy(() => import('./components/layout/ResidentLayout'))

import Layout from './components/layout/Layout'
import CommandPalette from './components/CommandPalette'

// Fallback dla Suspense — pokazywany podczas ładowania chunka strony
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  )
}

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const toggleCommandPalette = useUiStore((state) => state.toggleCommandPalette)
  const setCommandPaletteOpen = useUiStore((state) => state.setCommandPaletteOpen)

  const role = deriveRole(user)
  const isResident = role === 'resident'
  const isAdmin = isAuthenticated && !isResident

  // Global Cmd+K / Ctrl+K listener — paleta dostępna tylko dla adminów.
  useEffect(() => {
    if (!isAdmin) return
    const down = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggleCommandPalette()
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [isAdmin, toggleCommandPalette])

  // Po wylogowaniu zamknij paletę.
  useEffect(() => {
    if (!isAuthenticated) setCommandPaletteOpen(false)
  }, [isAuthenticated, setCommandPaletteOpen])

  // Landing — różne domyślne przekierowania dla admin vs resident.
  const landingDefault = () => {
    if (!isAuthenticated) return <LandingPage />
    return <Navigate to={isResident ? '/portal/dashboard' : '/dashboard'} replace />
  }

  const loginDefault = () => {
    if (!isAuthenticated) return <LoginPage />
    return <Navigate to={isResident ? '/portal/dashboard' : '/dashboard'} replace />
  }

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ---- Public ---- */}
          <Route path="/" element={landingDefault()} />
          <Route path="/login" element={loginDefault()} />

          {/* ---- Resident Portal — public entry ---- */}
          <Route
            path="/portal/login"
            element={
              isAuthenticated && isResident ? (
                <Navigate to="/portal/dashboard" replace />
              ) : (
                <ResidentLoginPage />
              )
            }
          />
          <Route path="/portal/activate" element={<ResidentActivatePage />} />

          {/* ---- Resident Portal — protected ---- */}
          <Route
            path="/portal"
            element={
              <ProtectedRoute
                requiredRole="resident"
                unauthenticatedRedirect="/portal/login"
                forbiddenRedirect="/dashboard"
              >
                <ResidentLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ResidentDashboardPage />} />
            <Route path="platnosci" element={<ResidentPaymentsPage />} />
            <Route path="liczniki" element={<ResidentMetersPage />} />
            <Route path="rozliczenia" element={<ResidentSettlementsPage />} />
            <Route path="ustawienia" element={<ResidentChangePasswordPage />} />
          </Route>

          {/* ---- Print View — outside Layout (admin scope) ---- */}
          <Route
            path="/rozliczenia/:id/print"
            element={
              <ProtectedRoute
                requiredRole={['superuser', 'admin']}
                unauthenticatedRedirect="/login"
                forbiddenRedirect="/portal/dashboard"
              >
                <RozliczeniePrintView />
              </ProtectedRoute>
            }
          />

          {/* ---- Admin (superuser + admin) inside Layout ---- */}
          <Route
            path="/*"
            element={
              <ProtectedRoute
                requiredRole={['superuser', 'admin']}
                unauthenticatedRedirect="/login"
                forbiddenRedirect="/portal/dashboard"
              >
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/wspolnoty" element={<WspolnotyPage />} />
                      <Route path="/wspolnoty/:id" element={<WspolnotaDetailPage />} />
                      <Route path="/wspolnoty/:id/edit" element={<WspolnotaEditPage />} />
                      <Route path="/administrator" element={<AdministratorPage />} />
                      <Route path="/lokale" element={<LokalePage />} />
                      <Route path="/lokale/:id" element={<LokalDetailPage />} />
                      <Route path="/lokale/:id/liczniki" element={<LicznikiPage />} />
                      <Route path="/liczniki" element={<LicznikiListPage />} />
                      <Route path="/rozliczenia" element={<RozliczeniaPage />} />
                      <Route path="/historia-rozliczen" element={<HistoriaRozliczenPage />} />
                      <Route path="/rozliczenia/:id" element={<RozliczenieDetailPage />} />
                      <Route path="/pozycje-czynszowe" element={<PozycjeCzynszowePage />} />
                      <Route path="/stawki-mediow" element={<StawkiMediowPage />} />
                      <Route path="/zadluzenia" element={<ZadluzeniaPage />} />
                      <Route path="/platnosci" element={<PlatnosciPage />} />
                      <Route path="/emaile" element={<EmailHistoryPage />} />
                      <Route path="/odczyty" element={<MasoweOdczytyPage />} />
                      <Route path="/import-bankowy" element={<ImportBankowyPage />} />
                      <Route path="/raporty" element={<RaportyPage />} />
                      <Route path="/powiadomienia" element={<PowiadomieniaPage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/audit-log" element={<AuditLogPage />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>

      {isAdmin && <CommandPalette />}
    </>
  )
}

export default App
